import React from 'react';
import { BulkZipJobView } from '../../../types/bulk-zip';
import { ContentsDisclosure } from './contents-disclosure.component';
import { excludedBySource } from '../bulk-zip-plan';
import styles from './downloads-center.component.module.scss';

interface Props {
    job: BulkZipJobView;
    onDownload: (jobId: string) => void;
    onDelete: (jobId: string) => void;
    onRetry: (jobId: string) => void;
}

interface State {
    showContents: boolean;
}

export class DownloadsCenterRow extends React.Component<Props, State> {
    state: State = { showContents: false };

    private toggle = () => this.setState((s) => ({ showContents: !s.showContents }));

    render() {
        const j = this.props.job;
        const isBuilding = j.processStage === 'BUILDING';
        const isReady = j.processStage === 'COMPLETED' && !j.failed && j.active;
        const isFailed = j.failed;
        // A job starts QUEUED, which matched none of the three states above, so a freshly
        // submitted job rendered as a bare filename: no status, no progress, no action. Nobody
        // saw it while the panel was only reachable from the header pill; opening the panel on
        // submit put that hole in front of every user, who then has to guess that waiting is
        // the right thing to do.
        const isWaiting = !isBuilding && !isReady && !isFailed;
        const pct =
            j.totalBytes > 0
                ? Math.min(100, Math.floor((j.bytesFlushed / j.totalBytes) * 100))
                : 0;

        return (
            <div className={styles['row']} data-status={j.processStage}>
                <div className={styles['title']}>
                    {isWaiting && <span>⏳ PREPARING </span>}
                    {isBuilding && <span>● BUILDING </span>}
                    {isReady && <span>✓ READY </span>}
                    {isFailed && <span>✕ FAILED </span>}
                    <code>{j.label}.zip</code>
                </div>

                {isWaiting && (
                    <div className={styles['caption']} role="status">
                        Getting your files together. Download appears here when it&apos;s ready —
                        you can keep using the site.
                    </div>
                )}

                {isBuilding && (
                    <div className={styles['progress']}>
                        <div className={styles['bar']}>
                            <span style={{ width: `${pct}%` }} />
                        </div>
                        <div className={styles['caption']}>
                            {pct}% — {j.currentFile ?? ''} ({j.filesCompleted}/{j.totalFiles})
                        </div>
                    </div>
                )}

                {isReady && (
                    <div className={styles['actions']}>
                        <button onClick={() => this.props.onDownload(j.jobId)}>Download</button>
                        <button
                            className={styles['icon']}
                            aria-label="Delete"
                            onClick={() => this.props.onDelete(j.jobId)}
                        >
                            🗑
                        </button>
                    </div>
                )}

                {isFailed && (
                    <div className={styles['actions']}>
                        <button onClick={() => this.props.onRetry(j.jobId)}>Retry</button>
                        <button
                            className={styles['icon']}
                            aria-label="Delete"
                            onClick={() => this.props.onDelete(j.jobId)}
                        >
                            🗑
                        </button>
                    </div>
                )}

                {j.excludedFiles.length > 0 && (
                    // Review 7 X1-05: say that these are elsewhere and where, grouped by the ready ZIP that holds them.
                    <div className={styles['excluded']} data-jkey="bulk-zip-excluded">
                        Not in this ZIP: {j.excludedFiles.length === 1 ? '1 file is' : `${j.excludedFiles.length} files are`} already
                        in a ZIP that is still ready to download.
                        <ul>
                            {excludedBySource(j.excludedFiles).map((g) => (
                                <li key={g.sourceJobId ?? g.label}>
                                    In{' '}
                                    {g.sourceJobId ? <a href={`#bz-${g.sourceJobId}`}>{g.label}</a> : g.label}: {g.names.join(', ')}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <button className={styles['disclosure']} onClick={this.toggle}>
                    {this.state.showContents ? '▾' : '▸'} Contents ({j.fileNames.length} files)
                </button>
                {this.state.showContents && <ContentsDisclosure fileNames={j.fileNames} />}
            </div>
        );
    }
}
